import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'rsuite';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { ActionIconButton } from '@commonClient2/components';
import UnawardedReportFilters from './UnawardedReportFilters';
import { loadUnawardedShiftlines, UPDATE_UNAWARDED_SHIFTLINES_REPORT } from '../../../store/unawardedShiftlinesReport';
import ShiftLineSelectionTable from '../../../Employee/components/ShiftLineSelectionTable';
import { ShiftlineSelectionTableType, BidProcessType, ShiftDisplayType } from '../../../Enums';
import { DAY_COLUMNS_COUNT } from '../../../Manager/BidProfileEdit/ScheduleSelection/ShiftlineTable/ShiftlineTable';
import './UnawardedShiftlinesReport.scss';

const pageSizeOptions = [10, 25, 50, 100];
const initialPageSize = 10;

/**
 * @param {number} page
 * @param {number} totalCount
 * @param {number} pageSize
 * @returns number
 */
const autoFixOutOfBound = (page, totalCount, pageSize) => Math.min(Math.max(page, 1), Math.ceil(totalCount / pageSize));

/**
 * @param {{
 *  onClose: Function
 * }} props
 */
function UnawardedShiftlinesReport({ onClose }) {
    const totalCount = useRef(0);
    const { t } = useTranslation('shiftBidding');
    const dispatch = useDispatch();

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { unawardedShiftlinesReport, bidProfile } = useSelector((state) => state);
    const { unawardedShiftlines, filters, shiftDisplayType = ShiftDisplayType.ShiftLabel } = unawardedShiftlinesReport;
    const { search, pageIndex, pageSize } = filters;
    const {
        anchorDay, bidProfileInformation, scheduleTemplates, profileId,
    } = bidProfile;
    const { scheduleSource, bidProcessType } = bidProfileInformation;
    const { shifts: shiftsDictionary, dayCount, periodType } = scheduleTemplates;
    const isPreferential = bidProcessType === BidProcessType.Preferential;

    /**
     * @type {[EmployeeBidProfile.IGroupedScheduleTemplateItems[], Function]} FilteredShiftlines
     */
    const [filteredShiftlines, setFilteredShiftlines] = useState(unawardedShiftlines);
    const resetSearchFilters = () => {
        dispatch({
            type: UPDATE_UNAWARDED_SHIFTLINES_REPORT,
            payload: {
                filters: {
                    search: '',
                    pageIndex: 1,
                    pageSize: parseInt(localStorage.getItem('unawardedShiftLineReportPageSize') || `${initialPageSize}`, 10),
                },
            },
        });
    };

    /**
     * @param {Partial<IUnawardedShiftlineReportFilters>} filterProps
     */
    const onChangeProperty = (filterProps) => {
        dispatch({
            type: UPDATE_UNAWARDED_SHIFTLINES_REPORT,
            payload: {
                filters: {
                    ...filters,
                    ...filterProps,
                },
            },
        });
    };

    const handleSearchButtonClick = (searchString) => {
        onChangeProperty({
            pageIndex: 1,
            search: searchString,
        });
    };

    const handlePageIndexChange = (index) => {
        index = autoFixOutOfBound(index, totalCount.current, pageSize);
        onChangeProperty({
            pageIndex: index,
        });
    };

    /**
     * @param {number} size
     */
    const handleChangePageSize = (size) => {
        localStorage.setItem('unawardedShiftLineReportPageSize', `${size}`);
        onChangeProperty({
            pageSize: size,
            pageIndex: autoFixOutOfBound(pageIndex, totalCount.current, size),
        });
    };

    useEffect(() => {
        dispatch(loadUnawardedShiftlines(profileId, search, pageIndex, pageSize))
            .then((promise) => {
                setFilteredShiftlines(promise.payload.unawardedShiftlines);
                totalCount.current = promise.payload.totalCount;
            }).catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters)]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => resetSearchFilters(), []);

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
     * @returns {number}
     */
    const calculateRowHeight = (items) => {
        const shiftlineSelectionRowPadding = 20;
        const shiftlineSelectionShiftHeight = 35;
        const shiftlineSelectionMinRowHeight = 55;

        const maxNbWeek = Math.max(...items.map((x) => x.nbWeek));
        const sumOfWeeklyMax = items.map((x) => x.weeklyHeight.reduce((prev, curr) => prev + (curr || 1), 0));
        const maxHeightUnits = Math.max(maxNbWeek, ...sumOfWeeklyMax);
        const calculatedRowHeight = shiftlineSelectionRowPadding + (maxHeightUnits * shiftlineSelectionShiftHeight);
        return Math.max(calculatedRowHeight, shiftlineSelectionMinRowHeight);
    };

    return (
        <>
            <div className="unawarded-shiftlines-report-header">
                <span className="header-title">{t('common.reports.unawardedShiftlines.title')}</span>
                <ActionIconButton icon={faTimes} onClick={onClose} />
            </div>
            <UnawardedReportFilters onSearchButtonClick={handleSearchButtonClick} />
            <ShiftLineSelectionTable
                isMultipleSelection={isPreferential}
                daysPerPeriod={DAY_COLUMNS_COUNT}
                type={ShiftlineSelectionTableType.UnawardedShiftlineReport}
                externalStoreData={{
                    filteredItems: filteredShiftlines,
                    shiftsDictionary,
                    scheduleSource,
                    anchorDay,
                    dayCount,
                    periodType,
                    shiftDisplayType,
                }}
                rowHeight={calculateRowHeight(filteredShiftlines)}
                height={600}
            />
            <div className="pagination-wrapper">
                <div className="pagination-desktop">
                    <Pagination
                        prev
                        next
                        first
                        last
                        ellipsis
                        boundaryLinks
                        maxButtons={5}
                        size="xs"
                        layout={['total', '-', 'pager', 'skip', '|', 'limit']}
                        total={totalCount.current}
                        limitOptions={pageSizeOptions}
                        limit={pageSize}
                        onChangeLimit={(limit) => handleChangePageSize(limit)}
                        activePage={pageIndex}
                        onChangePage={handlePageIndexChange}
                    />
                </div>
                <div className="pagination-mobile">
                    <div className="pagination-mobile-layout">
                        <Pagination
                            prev
                            next
                            first
                            last
                            ellipsis
                            boundaryLinks
                            maxButtons={3}
                            size="xs"
                            layout={['total', '-', 'pager']}
                            total={totalCount.current}
                            limit={pageSize}
                            activePage={pageIndex}
                            onChangePage={handlePageIndexChange}
                        />
                        <Pagination
                            size="xs"
                            layout={['skip', 'limit']}
                            total={totalCount.current}
                            limitOptions={pageSizeOptions}
                            limit={pageSize}
                            onChangeLimit={(limit) => handleChangePageSize(limit)}
                            activePage={pageIndex}
                            onChangePage={handlePageIndexChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default UnawardedShiftlinesReport;
